import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FINISHED_STATUSES } from 'utils/order/constants';
import {
  ORDER_STOP_DONE_STATUSES,
  ORDER_STOP_STATUSES,
} from 'utils/orderStop/constants';
import { formatters as loadFormatters } from 'utils/loads';
import {
  formatters as addressesFormatters,
  formatters as addressFormatters,
} from 'utils/addresses';
import { formatters as dateAndTimeFormatters } from 'utils/dateAndTimes';
import { getAppointmentDate } from 'utils/order';
import PropTypes from 'prop-types';
import {
  getArrivalTime,
  getIsCompleted,
  getIsLoadingOrUnloading,
  getIsOnWay,
  getLoads,
  getMaxHeight,
  getStandstillLabelFactory,
  getStandstillLabelParamsFactory,
  getStandstillType,
  getStopStatusLabel,
  getTimeInfo,
  getTotalWeight,
} from './UseOrderStop.utils';

const orderStopContext = createContext(null);
orderStopContext.displayName = 'orderStopContext';

const OrderStopContextProvider = ({
  stop,
  firstStop,
  order,
  isRecurring,
  isLastStop,
  children,
}) => {
  const utils = useOrderStopProvider(
    stop,
    firstStop,
    order,
    isRecurring,
    isLastStop
  );
  return (
    <orderStopContext.Provider value={utils}>
      {children}
    </orderStopContext.Provider>
  );
};

OrderStopContextProvider.propTypes = {
  stop: PropTypes.object.isRequired,
  firstStop: PropTypes.object.isRequired,
  order: PropTypes.object,
  isRecurring: PropTypes.bool,
  isLastStop: PropTypes.bool,
};

OrderStopContextProvider.defaultProps = {
  isRecurring: false,
  isLastStop: false,
};

const useOrderStopProvider = (
  stop,
  firstStop,
  order,
  isRecurring,
  isLastStop
) => {
  const { t } = useTranslation('OrderStop');
  const {
    on_route: summaryOnRoute,
    in_progress: summaryInProgress,
    done: summaryDone,
  } = stop.summary || {};
  const stopStatus = stop.status;

  const loads = getLoads(order, stop, isRecurring);

  const { valueFrom, valueTo, timezone } = getTimeInfo(
    stop,
    summaryInProgress,
    summaryDone
  );
  const isCompleted = getIsCompleted(stop);
  const isFinished = FINISHED_STATUSES.includes(stopStatus);
  const isDone = ORDER_STOP_DONE_STATUSES.includes(stopStatus);
  const isInProgress = stopStatus === ORDER_STOP_STATUSES.IN_PROGRESS;
  const isOnWay = getIsOnWay(summaryOnRoute, summaryInProgress, summaryDone);
  const isLoadingOrUnloading = getIsLoadingOrUnloading(
    summaryInProgress,
    summaryDone
  );
  const isStandstill = stop.standstill;
  const isPossibleStandstill = stop.possible_standstill;
  const stopType = stop.stop_type?.toLowerCase(); // pickup / dropoff
  const arrivalTime = getArrivalTime(stop);
  const getTotalHeightFormatted = (_loads) =>
    loadFormatters.formatMeasure({
      value: getMaxHeight(_loads),
      valueToNextUnit: 90,
      t,
    });
  const getTotalWeightFormatted = (_loads) => `${getTotalWeight(_loads)} kg`;
  const rawAddress = addressFormatters.getAddressLabel(stop.address);
  const addressFullData = addressesFormatters.getAddressFullData({
    rawAddress: rawAddress,
    addressLine: stop.address.address_line,
    zipcode: stop.address.zipcode,
    contactName: stop.address.contact_name,
    contactPhone: stop.address.contact_phone,
  });

  const stopStatusLabel = getStopStatusLabel({
    stopType,
    isDone,
    isPossibleStandstill,
    isStandstill,
    isOnWay,
    isLoadingOrUnloading,
  });
  const stopName = t(`label.${stopStatusLabel.value}`, {
    address: rawAddress,
  });

  const getFullDate = () => {
    if (isRecurring) {
      return dateAndTimeFormatters.getHumanReadableFormattedDateAndTimesForRecurring(
        {
          valueFrom,
          valueTo,
          timezone,
          dateFormat: 'recurring',
          stopType,
          firstStopInJourney: firstStop,
          t,
        }
      );
    }
    if (stopStatus === ORDER_STOP_STATUSES.IN_PROGRESS) {
      return t('label.now');
    }
    const stopDate = dateAndTimeFormatters.getHumanReadableFormattedDate({
      value: valueFrom,
      timezone,
      format: 'short',
      t,
    });
    const stopTimes = dateAndTimeFormatters
      .getHumanReadableFormattedTimes({
        valueFrom,
        valueTo,
        timezone,
      })
      .split(' - ');
    return t(
      isCompleted ? `date_with_status.${stopType}` : `date.${stopType}`,
      {
        date: stopDate,
        time_from: stopTimes[0],
        time_to: stopTimes[1],
      }
    );
  };

  const appointmentDate = getAppointmentDate({
    stopCompleted: isCompleted,
    appointment: stop.appointment,
    timezone,
    t,
  });

  const getStandstillLabelParams = getStandstillLabelParamsFactory({
    valueFrom,
    timezone,
    summaryDone,
    arrivalTime,
    t,
  });

  const getStandstillLabel = getStandstillLabelFactory({
    isInProgress,
    isStandstill,
    isPossibleStandstill,
    stopType,
    isDone,
    order,
    t,
    getStandstillLabelParams,
  });

  const standstillType = getStandstillType(arrivalTime, stop);

  return {
    stop,
    stopType,
    loads,
    addressFullData,
    getFullDate,
    isFinished,
    isDone,
    isInProgress,
    isOnWay,
    isLoadingOrUnloading,
    isStandstill,
    isPossibleStandstill,
    isLastStop,
    stopName,
    appointmentDate,
    getStandstillLabel,
    getStandstillLabelParams,
    getTotalHeightFormatted,
    getTotalWeightFormatted,
    standstillType,
  };
};

export const useOrderStop = () => {
  return useContext(orderStopContext);
};

export default OrderStopContextProvider;
